import { cn } from '@elsapiens/utils'

interface IllustrationProps {
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

const sizeClasses = {
  sm: 'w-32 h-32',
  md: 'w-48 h-48',
  lg: 'w-56 h-56',
}

export function JournalEntryIllustration({ className, size = 'lg' }: IllustrationProps): JSX.Element {
  return (
    <svg className={cn(sizeClasses[size], className)} fill="none" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
      <circle className="fill-primary/[0.03] dark:fill-primary/10" cx="100" cy="100" r="85" />
      <circle className="fill-primary/[0.05] dark:fill-primary/15" cx="100" cy="100" r="70" />
      {/* Open book / ledger */}
      <rect className="fill-background stroke-primary/30 dark:stroke-primary/45" height="100" rx="4" strokeWidth="2" width="55" x="40" y="50" />
      <rect className="fill-background stroke-primary/30 dark:stroke-primary/45" height="100" rx="4" strokeWidth="2" width="55" x="105" y="50" />
      {/* Spine */}
      <line className="stroke-primary/40 dark:stroke-primary/55" strokeWidth="2" x1="97.5" x2="97.5" y1="50" y2="150" />
      {/* Left page - Debit lines */}
      <rect className="fill-primary/25 dark:fill-primary/40" height="3" rx="1.5" width="20" x="48" y="62" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="35" x="48" y="72" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="28" x="48" y="82" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="32" x="48" y="92" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="25" x="48" y="102" />
      {/* Left amounts */}
      <rect className="fill-primary/30 dark:fill-primary/45" height="3" rx="1.5" width="12" x="78" y="72" />
      <rect className="fill-primary/30 dark:fill-primary/45" height="3" rx="1.5" width="10" x="78" y="82" />
      <rect className="fill-primary/30 dark:fill-primary/45" height="3" rx="1.5" width="12" x="78" y="92" />
      {/* Right page - Credit lines */}
      <rect className="fill-primary/25 dark:fill-primary/40" height="3" rx="1.5" width="20" x="113" y="62" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="30" x="113" y="72" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="35" x="113" y="82" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="28" x="113" y="92" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="32" x="113" y="102" />
      {/* Right amounts */}
      <rect className="fill-primary/30 dark:fill-primary/45" height="3" rx="1.5" width="10" x="143" y="72" />
      <rect className="fill-primary/30 dark:fill-primary/45" height="3" rx="1.5" width="12" x="143" y="82" />
      <rect className="fill-primary/30 dark:fill-primary/45" height="3" rx="1.5" width="11" x="143" y="92" />
      {/* Total line */}
      <line className="stroke-primary/25 dark:stroke-primary/40" strokeWidth="1" x1="48" x2="90" y1="118" y2="118" />
      <rect className="fill-primary/40 dark:fill-primary/55" height="3" rx="1.5" width="12" x="78" y="122" />
      <line className="stroke-primary/25 dark:stroke-primary/40" strokeWidth="1" x1="113" x2="155" y1="118" y2="118" />
      <rect className="fill-primary/40 dark:fill-primary/55" height="3" rx="1.5" width="12" x="143" y="122" />
      {/* Decorative */}
      <circle className="fill-primary/15 dark:fill-primary/30" cx="30" cy="60" r="4" />
      <circle className="fill-primary/10 dark:fill-primary/25" cx="170" cy="140" r="3" />
    </svg>
  )
}

export function DashboardIllustration({ className, size = 'lg' }: IllustrationProps): JSX.Element {
  return (
    <svg className={cn(sizeClasses[size], className)} fill="none" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
      <circle className="fill-primary/[0.03] dark:fill-primary/10" cx="100" cy="100" r="85" />
      <circle className="fill-primary/[0.05] dark:fill-primary/15" cx="100" cy="100" r="70" />
      {/* Dashboard panel */}
      <rect className="fill-primary/10 stroke-primary/40 dark:stroke-primary/55" height="100" rx="8" strokeWidth="2" width="110" x="45" y="50" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="18" rx="8" width="110" x="45" y="50" />
      <circle className="fill-red-400/40" cx="58" cy="59" r="3" />
      <circle className="fill-yellow-400/40" cx="68" cy="59" r="3" />
      <circle className="fill-green-400/40" cx="78" cy="59" r="3" />
      {/* Stat cards */}
      <rect className="fill-background stroke-primary/20 dark:stroke-primary/35" height="25" rx="4" strokeWidth="1.5" width="45" x="53" y="75" />
      <rect className="fill-background stroke-primary/20 dark:stroke-primary/35" height="25" rx="4" strokeWidth="1.5" width="45" x="102" y="75" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="20" x="58" y="80" />
      <rect className="fill-primary/35 dark:fill-primary/50" height="5" rx="2" width="30" x="58" y="87" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="20" x="107" y="80" />
      <rect className="fill-primary/35 dark:fill-primary/50" height="5" rx="2" width="25" x="107" y="87" />
      {/* Bar chart */}
      <rect className="fill-background stroke-primary/20 dark:stroke-primary/35" height="35" rx="4" strokeWidth="1.5" width="94" x="53" y="107" />
      <rect className="fill-primary/30 dark:fill-primary/45" height="8" rx="1.5" width="8" x="62" y="128" />
      <rect className="fill-primary/40 dark:fill-primary/55" height="16" rx="1.5" width="8" x="74" y="120" />
      <rect className="fill-primary/50 dark:fill-primary/65" height="21" rx="1.5" width="8" x="86" y="115" />
      <rect className="fill-primary/35 dark:fill-primary/50" height="14" rx="1.5" width="8" x="98" y="122" />
      <rect className="fill-primary/45 dark:fill-primary/60" height="18" rx="1.5" width="8" x="110" y="118" />
      <rect className="fill-primary/25 dark:fill-primary/40" height="11" rx="1.5" width="8" x="122" y="125" />
      <circle className="fill-primary/15 dark:fill-primary/30" cx="35" cy="55" r="4" />
      <circle className="fill-primary/10 dark:fill-primary/25" cx="165" cy="145" r="3" />
    </svg>
  )
}

export function ChartOfAccountsIllustration({ className, size = 'lg' }: IllustrationProps): JSX.Element {
  return (
    <svg className={cn(sizeClasses[size], className)} fill="none" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
      <circle className="fill-primary/[0.03] dark:fill-primary/10" cx="100" cy="100" r="85" />
      <circle className="fill-primary/[0.05] dark:fill-primary/15" cx="100" cy="100" r="70" />
      {/* Tree structure */}
      <rect className="fill-primary/20 stroke-primary/40 dark:stroke-primary/55" height="18" rx="4" strokeWidth="1.5" width="50" x="75" y="40" />
      <rect className="fill-primary/30 dark:fill-primary/45" height="3" rx="1.5" width="30" x="80" y="46" />
      {/* Branches */}
      <line className="stroke-primary/30 dark:stroke-primary/45" strokeWidth="1.5" x1="100" x2="100" y1="58" y2="68" />
      <line className="stroke-primary/30 dark:stroke-primary/45" strokeWidth="1.5" x1="60" x2="140" y1="68" y2="68" />
      <line className="stroke-primary/30 dark:stroke-primary/45" strokeWidth="1.5" x1="60" x2="60" y1="68" y2="75" />
      <line className="stroke-primary/30 dark:stroke-primary/45" strokeWidth="1.5" x1="100" x2="100" y1="68" y2="75" />
      <line className="stroke-primary/30 dark:stroke-primary/45" strokeWidth="1.5" x1="140" x2="140" y1="68" y2="75" />
      {/* Level 2 */}
      <rect className="fill-background stroke-primary/25 dark:stroke-primary/40" height="16" rx="3" strokeWidth="1.5" width="40" x="40" y="75" />
      <rect className="fill-background stroke-primary/25 dark:stroke-primary/40" height="16" rx="3" strokeWidth="1.5" width="40" x="80" y="75" />
      <rect className="fill-background stroke-primary/25 dark:stroke-primary/40" height="16" rx="3" strokeWidth="1.5" width="40" x="120" y="75" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="25" x="45" y="80" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="25" x="85" y="80" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="25" x="125" y="80" />
      {/* Level 3 sub-items */}
      <line className="stroke-primary/20 dark:stroke-primary/35" strokeWidth="1.5" x1="60" x2="60" y1="91" y2="100" />
      <rect className="fill-background stroke-primary/15 dark:stroke-primary/30" height="12" rx="2" strokeWidth="1" width="30" x="45" y="100" />
      <rect className="fill-background stroke-primary/15 dark:stroke-primary/30" height="12" rx="2" strokeWidth="1" width="30" x="45" y="116" />
      <line className="stroke-primary/20 dark:stroke-primary/35" strokeWidth="1.5" x1="100" x2="100" y1="91" y2="100" />
      <rect className="fill-background stroke-primary/15 dark:stroke-primary/30" height="12" rx="2" strokeWidth="1" width="30" x="85" y="100" />
      <rect className="fill-background stroke-primary/15 dark:stroke-primary/30" height="12" rx="2" strokeWidth="1" width="30" x="85" y="116" />
      <rect className="fill-background stroke-primary/15 dark:stroke-primary/30" height="12" rx="2" strokeWidth="1" width="30" x="85" y="132" />
      <line className="stroke-primary/20 dark:stroke-primary/35" strokeWidth="1.5" x1="140" x2="140" y1="91" y2="100" />
      <rect className="fill-background stroke-primary/15 dark:stroke-primary/30" height="12" rx="2" strokeWidth="1" width="30" x="125" y="100" />
      <circle className="fill-primary/15 dark:fill-primary/30" cx="30" cy="50" r="3" />
      <circle className="fill-primary/10 dark:fill-primary/25" cx="170" cy="130" r="4" />
    </svg>
  )
}

export function InvoiceIllustration({ className, size = 'lg' }: IllustrationProps): JSX.Element {
  return (
    <svg className={cn(sizeClasses[size], className)} fill="none" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
      <circle className="fill-primary/[0.03] dark:fill-primary/10" cx="100" cy="100" r="85" />
      <circle className="fill-primary/[0.05] dark:fill-primary/15" cx="100" cy="100" r="70" />
      {/* Invoice paper */}
      <rect className="fill-background stroke-primary/30 dark:stroke-primary/45" height="130" rx="6" strokeWidth="2" width="90" x="55" y="35" />
      {/* Header */}
      <rect className="fill-primary/15 dark:fill-primary/30" height="10" rx="3" width="25" x="65" y="45" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="35" x="100" y="47" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="25" x="100" y="53" />
      {/* Separator */}
      <line className="stroke-primary/20 dark:stroke-primary/35" strokeWidth="1" x1="65" x2="135" y1="65" y2="65" />
      {/* Line items header */}
      <rect className="fill-primary/25 dark:fill-primary/40" height="3" rx="1.5" width="20" x="65" y="72" />
      <rect className="fill-primary/25 dark:fill-primary/40" height="3" rx="1.5" width="15" x="95" y="72" />
      <rect className="fill-primary/25 dark:fill-primary/40" height="3" rx="1.5" width="15" x="120" y="72" />
      {/* Line items */}
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="25" x="65" y="82" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="12" x="95" y="82" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="15" x="120" y="82" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="30" x="65" y="92" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="10" x="95" y="92" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="12" x="120" y="92" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="22" x="65" y="102" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="14" x="95" y="102" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="15" x="120" y="102" />
      {/* Total line */}
      <line className="stroke-primary/30 dark:stroke-primary/45" strokeWidth="1" x1="95" x2="135" y1="115" y2="115" />
      <rect className="fill-primary/25 dark:fill-primary/40" height="3" rx="1.5" width="15" x="95" y="120" />
      <rect className="fill-primary/35 dark:fill-primary/50" height="5" rx="2" width="17" x="118" y="119" />
      {/* Stamp / badge */}
      <rect className="fill-primary/10 stroke-primary/25 dark:stroke-primary/40" height="12" rx="3" strokeDasharray="3 2" strokeWidth="1" width="30" x="65" y="135" />
      <circle className="fill-primary/15 dark:fill-primary/30" cx="35" cy="55" r="4" />
      <circle className="fill-primary/10 dark:fill-primary/25" cx="160" cy="150" r="3" />
    </svg>
  )
}

export function ReportIllustration({ className, size = 'lg' }: IllustrationProps): JSX.Element {
  return (
    <svg className={cn(sizeClasses[size], className)} fill="none" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
      <circle className="fill-primary/[0.03] dark:fill-primary/10" cx="100" cy="100" r="85" />
      <circle className="fill-primary/[0.05] dark:fill-primary/15" cx="100" cy="100" r="70" />
      {/* Report page */}
      <rect className="fill-background stroke-primary/30 dark:stroke-primary/45" height="120" rx="6" strokeWidth="2" width="100" x="50" y="40" />
      {/* Title */}
      <rect className="fill-primary/25 dark:fill-primary/40" height="4" rx="2" width="40" x="60" y="50" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="60" x="60" y="58" />
      {/* Pie chart */}
      <circle className="fill-primary/10 stroke-primary/30 dark:stroke-primary/45" cx="85" cy="85" r="18" strokeWidth="2" />
      <path className="fill-primary/25 stroke-primary/40 dark:stroke-primary/55" d="M85 67 L85 85 L100 78" strokeWidth="1.5" />
      {/* Bar chart */}
      <rect className="fill-primary/30 dark:fill-primary/45" height="14" rx="1.5" width="6" x="112" y="78" />
      <rect className="fill-primary/40 dark:fill-primary/55" height="20" rx="1.5" width="6" x="121" y="72" />
      <rect className="fill-primary/25 dark:fill-primary/40" height="10" rx="1.5" width="6" x="130" y="82" />
      {/* Table rows */}
      <line className="stroke-primary/15 dark:stroke-primary/30" strokeWidth="1" x1="60" x2="140" y1="112" y2="112" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="30" x="60" y="116" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="20" x="110" y="116" />
      <line className="stroke-primary/15 dark:stroke-primary/30" strokeWidth="1" x1="60" x2="140" y1="124" y2="124" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="25" x="60" y="128" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="25" x="110" y="128" />
      <line className="stroke-primary/15 dark:stroke-primary/30" strokeWidth="1" x1="60" x2="140" y1="136" y2="136" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="35" x="60" y="140" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="18" x="110" y="140" />
      <circle className="fill-primary/15 dark:fill-primary/30" cx="40" cy="50" r="3" />
      <circle className="fill-primary/10 dark:fill-primary/25" cx="165" cy="145" r="4" />
    </svg>
  )
}

export function CustomerIllustration({ className, size = 'lg' }: IllustrationProps): JSX.Element {
  return (
    <svg className={cn(sizeClasses[size], className)} fill="none" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
      <circle className="fill-primary/[0.03] dark:fill-primary/10" cx="100" cy="100" r="85" />
      <circle className="fill-primary/[0.05] dark:fill-primary/15" cx="100" cy="100" r="70" />
      {/* Central person */}
      <circle className="fill-primary/10 stroke-primary/30 dark:stroke-primary/45" cx="100" cy="70" r="18" strokeWidth="2" />
      <circle className="fill-primary/15 dark:fill-primary/30" cx="100" cy="65" r="8" />
      <path className="stroke-primary/25 dark:stroke-primary/40" d="M85 78 Q100 90 115 78" fill="none" strokeWidth="2" />
      {/* Card */}
      <rect className="fill-background stroke-primary/30 dark:stroke-primary/45" height="55" rx="6" strokeWidth="2" width="80" x="60" y="95" />
      <rect className="fill-primary/25 dark:fill-primary/40" height="3" rx="1.5" width="35" x="70" y="105" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="50" x="70" y="112" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="40" x="70" y="119" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="45" x="70" y="126" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="30" x="70" y="133" />
      {/* Side people */}
      <circle className="fill-primary/[0.06] dark:fill-primary/20 stroke-primary/20 dark:stroke-primary/35" cx="45" cy="90" r="10" strokeWidth="1.5" />
      <circle className="fill-primary/10 dark:fill-primary/25" cx="45" cy="87" r="4" />
      <circle className="fill-primary/[0.06] dark:fill-primary/20 stroke-primary/20 dark:stroke-primary/35" cx="155" cy="90" r="10" strokeWidth="1.5" />
      <circle className="fill-primary/10 dark:fill-primary/25" cx="155" cy="87" r="4" />
      <circle className="fill-primary/15 dark:fill-primary/30" cx="30" cy="55" r="3" />
      <circle className="fill-primary/10 dark:fill-primary/25" cx="170" cy="140" r="4" />
    </svg>
  )
}

export function PaymentIllustration({ className, size = 'lg' }: IllustrationProps): JSX.Element {
  return (
    <svg className={cn(sizeClasses[size], className)} fill="none" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
      <circle className="fill-primary/[0.03] dark:fill-primary/10" cx="100" cy="100" r="85" />
      <circle className="fill-primary/[0.05] dark:fill-primary/15" cx="100" cy="100" r="70" />
      {/* Credit card */}
      <rect className="fill-primary/10 stroke-primary/30 dark:stroke-primary/45" height="70" rx="8" strokeWidth="2" width="110" x="45" y="60" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="20" rx="8" width="110" x="45" y="60" />
      {/* Chip */}
      <rect className="fill-primary/15 stroke-primary/25 dark:stroke-primary/40" height="14" rx="3" strokeWidth="1.5" width="18" x="60" y="90" />
      <line className="stroke-primary/20 dark:stroke-primary/35" strokeWidth="1" x1="60" x2="78" y1="97" y2="97" />
      <line className="stroke-primary/20 dark:stroke-primary/35" strokeWidth="1" x1="69" x2="69" y1="90" y2="104" />
      {/* Card number */}
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="20" x="60" y="112" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="20" x="85" y="112" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="3" rx="1.5" width="20" x="110" y="112" />
      {/* Arrow */}
      <path className="stroke-primary/40 dark:stroke-primary/55" d="M100 145 L100 165 M92 158 L100 166 L108 158" strokeLinecap="round" strokeWidth="2" />
      <circle className="fill-primary/15 dark:fill-primary/30" cx="35" cy="55" r="4" />
      <circle className="fill-primary/10 dark:fill-primary/25" cx="165" cy="150" r="3" />
    </svg>
  )
}

export function CurrencyIllustration({ className, size = 'lg' }: IllustrationProps): JSX.Element {
  return (
    <svg className={cn(sizeClasses[size], className)} fill="none" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
      <circle className="fill-primary/[0.03] dark:fill-primary/10" cx="100" cy="100" r="85" />
      <circle className="fill-primary/[0.05] dark:fill-primary/15" cx="100" cy="100" r="70" />
      {/* Left coin */}
      <circle className="fill-primary/10 stroke-primary/30 dark:stroke-primary/45" cx="80" cy="90" r="30" strokeWidth="2" />
      <circle className="stroke-primary/20 dark:stroke-primary/35" cx="80" cy="90" r="22" strokeWidth="1.5" />
      <text className="fill-primary/40 dark:fill-primary/55" fontSize="20" fontWeight="bold" textAnchor="middle" x="80" y="96">$</text>
      {/* Right coin */}
      <circle className="fill-primary/10 stroke-primary/30 dark:stroke-primary/45" cx="125" cy="100" r="30" strokeWidth="2" />
      <circle className="stroke-primary/20 dark:stroke-primary/35" cx="125" cy="100" r="22" strokeWidth="1.5" />
      <text className="fill-primary/40 dark:fill-primary/55" fontSize="18" fontWeight="bold" textAnchor="middle" x="125" y="106">&euro;</text>
      {/* Exchange arrows */}
      <path className="stroke-primary/35 dark:stroke-primary/50" d="M90 55 L115 55 M110 50 L116 55 L110 60" strokeLinecap="round" strokeWidth="2" />
      <path className="stroke-primary/35 dark:stroke-primary/50" d="M115 145 L90 145 M95 140 L89 145 L95 150" strokeLinecap="round" strokeWidth="2" />
      <circle className="fill-primary/15 dark:fill-primary/30" cx="35" cy="60" r="3" />
      <circle className="fill-primary/10 dark:fill-primary/25" cx="165" cy="140" r="4" />
    </svg>
  )
}

export function SettingsIllustration({ className, size = 'lg' }: IllustrationProps): JSX.Element {
  return (
    <svg className={cn(sizeClasses[size], className)} fill="none" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
      <circle className="fill-primary/[0.03] dark:fill-primary/10" cx="100" cy="100" r="85" />
      <circle className="fill-primary/[0.05] dark:fill-primary/15" cx="100" cy="100" r="70" />
      {/* Gear */}
      <circle className="fill-primary/10 stroke-primary/30 dark:stroke-primary/45" cx="100" cy="95" r="25" strokeWidth="2" />
      <circle className="fill-background stroke-primary/25 dark:stroke-primary/40" cx="100" cy="95" r="12" strokeWidth="2" />
      {/* Gear teeth */}
      <rect className="fill-primary/20 dark:fill-primary/35" height="10" rx="2" width="8" x="96" y="66" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="10" rx="2" width="8" x="96" y="114" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="8" rx="2" width="10" x="71" y="91" />
      <rect className="fill-primary/20 dark:fill-primary/35" height="8" rx="2" width="10" x="119" y="91" />
      {/* Sliders */}
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="90" x="55" y="140" />
      <circle className="fill-primary/25 stroke-primary/35 dark:stroke-primary/50" cx="85" cy="141.5" r="5" strokeWidth="1.5" />
      <rect className="fill-primary/15 dark:fill-primary/30" height="3" rx="1.5" width="90" x="55" y="152" />
      <circle className="fill-primary/25 stroke-primary/35 dark:stroke-primary/50" cx="115" cy="153.5" r="5" strokeWidth="1.5" />
      <circle className="fill-primary/15 dark:fill-primary/30" cx="35" cy="55" r="3" />
      <circle className="fill-primary/10 dark:fill-primary/25" cx="165" cy="145" r="4" />
    </svg>
  )
}
